<template>
  <div v-show="isOpen" class="camera-viewer-overlay" @click.self="handleClose">
    <div class="camera-viewer-container">
      <div class="camera-viewer-header">
        <h2>{{ cameraActive ? 'Prendre une photo' : 'Ajouter une photo' }}</h2>
        <button class="close-button" @click="handleClose" aria-label="Fermer">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="camera-viewer-content">
        <!-- Message d'erreur -->
        <div v-if="error" class="error-message">
          <i class="fas fa-exclamation-triangle"></i>
          <p>{{ error }}</p>
        </div>

        <!-- Vidéo de la caméra en temps réel -->
        <div v-if="cameraActive && !capturedImage" class="camera-preview">
          <video ref="videoElement" autoplay playsinline muted></video>
          <!-- Indicateur de résolution -->
          <div class="camera-info" v-if="videoResolution">
            <i class="fas fa-video"></i> {{ videoResolution }}
          </div>
          <!-- Bouton de basculement de caméra -->
          <button 
            v-if="canSwitchCamera" 
            class="switch-camera-button" 
            @click="switchCamera"
            :disabled="isRequestingCamera"
            aria-label="Changer de caméra"
          >
            <i class="fas fa-sync-alt"></i>
            <span>{{ currentFacingMode === 'environment' ? 'Caméra avant' : 'Caméra arrière' }}</span>
          </button>
        </div>

        <!-- Image capturée avec watermark -->
        <div v-if="capturedImage" class="captured-preview">
          <img :src="capturedImage" alt="Photo capturée" />
        </div>

        <!-- Interface de sélection de fichier si pas de caméra (uniquement pour utilisateurs autorisés) -->
        <div v-if="!cameraActive && !capturedImage && !isRequestingCamera && canSelectFile" class="file-selection">
          <div class="upload-zone" @click="triggerFileInput">
            <i class="fas fa-image fa-3x"></i>
            <p>Cliquez pour sélectionner une photo</p>
            <p class="help-text">ou glissez-déposez un fichier ici</p>
          </div>
          <input 
            ref="fileInput" 
            type="file" 
            accept="image/*" 
            @change="handleFileSelect"
            style="display: none;"
          />
        </div>

        <!-- Message pour utilisateurs non autorisés -->
        <div v-if="!cameraActive && !capturedImage && !isRequestingCamera && !canSelectFile" class="file-selection">
          <div class="upload-zone disabled">
            <i class="fas fa-camera fa-3x"></i>
            <p>Veuillez utiliser la caméra pour prendre une photo</p>
            <p class="help-text">La sélection de fichiers n'est pas disponible</p>
          </div>
        </div>

        <!-- Indicateur de chargement -->
        <div v-if="isRequestingCamera" class="loading-indicator">
          <div class="spinner"></div>
          <p>Initialisation de la caméra...</p>
        </div>
      </div>

      <div class="camera-viewer-actions">
        <!-- Actions pour la caméra active -->
        <template v-if="cameraActive && !capturedImage">
          <button v-if="canSelectFile" class="btn btn-secondary" @click="switchToFileSelection">
            <i class="fas fa-folder-open"></i>
            Sélectionner un fichier
          </button>
          <button class="btn btn-primview" @click="capturePhoto">
            <i class="fas fa-camera"></i>
            Capturer
          </button>
        </template>

        <!-- Actions pour image capturée -->
        <template v-if="capturedImage">
          <button class="btn btn-secondary" @click="retakePhoto">
            <i class="fas fa-redo"></i>
            Reprendre
          </button>
          <button class="btn btn-primview" @click="confirmPhoto">
            <i class="fas fa-check"></i>
            Valider
          </button>
        </template>

        <!-- Actions pour sélection de fichier -->
        <template v-if="!cameraActive && !capturedImage && !isRequestingCamera">
          <button class="btn btn-secondary" @click="requestCameraAccess">
            <i class="fas fa-camera"></i>
            Utiliser la caméra
          </button>
        </template>

        <!-- Bouton annuler toujours présent -->
        <button v-if="!isRequestingCamera" class="btn btn-cancel" @click="handleClose">
          Annuler
        </button>
      </div>
    </div>

    <!-- Canvas pour la capture et le watermark -->
    <canvas ref="canvasElement" style="display: none;"></canvas>
  </div>
</template>

<script>
import { mapState } from 'vuex'

export default {
  name: 'CameraViewer',
  
  data() {
    return {
      cameraActive: false,
      capturedImage: null,
      error: null,
      stream: null,
      isRequestingCamera: false,
      currentContext: null, // { id, isRestoration, isAction }
      videoResolution: null,
      currentFacingMode: 'environment', // 'environment' (arrière) ou 'user' (avant) — arrière par défaut
      availableCameras: [],   // Liste des caméras disponibles
      rearCameraDeviceId: null,  // deviceId de la caméra arrière (identifiée par label)
      frontCameraDeviceId: null  // deviceId de la caméra avant (identifiée par label)
    }
  },
  
  computed: {
    ...mapState('cameraViewer', {
      viewerData: 'data'
    }),
    
    ...mapState('login', {
      currentUser: 'user'
    }),
    
    isOpen() {
      return this.viewerData !== null
    },
    
    // Vérifie si l'utilisateur peut sélectionner un fichier depuis l'ordinateur
    canSelectFile() {
      if (!this.currentUser || !this.currentUser.username) {
        return false
      }
      const allowedUsers = ['boris.parejo', 'igor.davidovitch']
      return allowedUsers.includes(this.currentUser.username)
    },
    
    // Afficher le bouton switch dès qu'on a détecté plusieurs caméras
    canSwitchCamera() {
      return this.availableCameras.length > 1
    }
  },
  
  watch: {
    viewerData: {
      handler(newData, oldData) {
        if (newData && newData !== oldData) {
          this.openViewer(newData)
        } else if (!newData) {
          this.resetViewer()
        }
      },
      deep: true,
      immediate: true
    }
  },
  
  methods: {
    async openViewer(context) {
      this.currentContext = context
      this.error = null
      this.capturedImage = null
      this.cameraActive = false
      this.isRequestingCamera = false
      this.videoResolution = null
      this.currentFacingMode = 'environment' // Toujours démarrer avec la caméra arrière
      this.availableCameras = [] // Réinitialiser pour forcer une nouvelle énumération
      this.rearCameraDeviceId = null  // Réinitialiser pour re-identifier au prochain accès
      this.frontCameraDeviceId = null
      
      // Attendre le prochain tick pour être sûr que le DOM est mis à jour
      await this.$nextTick()
      
      // Vérifier si l'accès caméra est possible avant de l'essayer automatiquement
      const canAccessCamera = this.isCameraAccessPossible()
      
      if (canAccessCamera) {
        await this.requestCameraAccess()
      } else {
        // Si pas de contexte sécurisé, afficher un message informatif
        this.error = 'L\'accès à la caméra nécessite une connexion HTTPS sécurisée. Vous pouvez sélectionner une photo depuis vos fichiers.'
        this.cameraActive = false
        
        // Forcer le rendu de l'interface
        await this.$nextTick()
      }
    },
    
    resetViewer() {
      this.stopCamera()
      this.capturedImage = null
      this.error = null
      this.currentContext = null
      this.cameraActive = false
      this.isRequestingCamera = false
      this.videoResolution = null
      this.currentFacingMode = 'environment' // Réinitialiser à la caméra arrière
      this.availableCameras = []
      this.rearCameraDeviceId = null
      this.frontCameraDeviceId = null
    },
    
    isCameraAccessPossible() {
      // Vérifier si on est en HTTPS ou localhost (requis pour getUserMedia)
      const isSecureContext = window.isSecureContext || 
                             window.location.protocol === 'https:' || 
                             window.location.hostname === 'localhost' ||
                             window.location.hostname === '127.0.0.1'
      
      if (!isSecureContext) {
        console.warn('Contexte non sécurisé - HTTPS requis pour accéder à la caméra')
        return false
      }
      
      // Vérifier l'API moderne
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        return true
      }
      
      // Vérifier l'ancienne API (webkitGetUserMedia, mozGetUserMedia)
      const legacyGetUserMedia = navigator.getUserMedia || 
                                 navigator.webkitGetUserMedia || 
                                 navigator.mozGetUserMedia || 
                                 navigator.msGetUserMedia
      
      if (legacyGetUserMedia) {
        return true
      }
      
      console.warn('Aucune API getUserMedia disponible')
      return false
    },
    
    async getUserMediaLegacy(constraints) {
      // Pour les navigateurs anciens qui utilisent les API préfixées
      return new Promise((resolve, reject) => {
        const legacyGetUserMedia = navigator.getUserMedia || 
                                   navigator.webkitGetUserMedia || 
                                   navigator.mozGetUserMedia || 
                                   navigator.msGetUserMedia
        
        if (!legacyGetUserMedia) {
          reject(new Error('API getUserMedia non disponible'))
          return
        }
        
        legacyGetUserMedia.call(navigator, constraints, resolve, reject)
      })
    },
    
    async enumerateCameras() {
      // Énumérer les caméras disponibles (avec labels si permission déjà accordée)
      try {
        if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
          const devices = await navigator.mediaDevices.enumerateDevices()
          this.availableCameras = devices.filter(device => device.kind === 'videoinput')

          return this.availableCameras
        }
      } catch (err) {
        console.warn('Impossible d\'énumérer les caméras:', err)
      }
      return []
    },

    /**
     * Identifie la caméra arrière et la caméra avant par leur label.
     * Nécessaire pour Windows Surface qui ne supporte pas facingMode.
     * Sur Surface: "Back Camera" / "Front Camera" ou "Rear Camera" / "Front Camera"
     */
    identifyCamerasByLabel(cameras) {
      if (cameras.length < 2) return

      const rearKeywords = ['back', 'rear', 'arrière', 'environment', 'world-facing', 'back-facing', 'back camera']
      const frontKeywords = ['front', 'user', 'selfie', 'avant', 'face', 'user-facing', 'front camera', 'face camera']

      // Chercher la caméra arrière par label
      let rearCam = cameras.find(cam => {
        const label = (cam.label || '').toLowerCase()
        return rearKeywords.some(kw => label.includes(kw))
      })

      // Chercher la caméra avant par label
      let frontCam = cameras.find(cam => {
        const label = (cam.label || '').toLowerCase()
        return frontKeywords.some(kw => label.includes(kw))
      })

      // Fallback si pas de label reconnu (Surface peut avoir des labels génériques)
      // Sur Windows: la caméra AVANT est généralement en premier, l'ARRIÈRE en dernier
      if (!rearCam && cameras.length > 1) {
        rearCam = cameras[cameras.length - 1]
      }
      if (!frontCam && cameras.length > 0) {
        // Prendre la première caméra différente de la caméra arrière
        frontCam = cameras.find(cam => cam.deviceId !== rearCam?.deviceId) || cameras[0]
      }

      if (rearCam) {
        this.rearCameraDeviceId = rearCam.deviceId
      }
      if (frontCam && frontCam.deviceId !== rearCam?.deviceId) {
        this.frontCameraDeviceId = frontCam.deviceId
      }
    },

    /**
     * Retourne la contrainte vidéo optimale pour la caméra demandée.
     * Priorité: deviceId (fiable sur Windows) > facingMode (mobile)
     */
    buildVideoConstraints() {
      const targetDeviceId = this.currentFacingMode === 'environment'
        ? this.rearCameraDeviceId
        : this.frontCameraDeviceId

      if (targetDeviceId) {
        // Sur Windows Surface: utiliser deviceId est la SEULE méthode fiable
        return { deviceId: { exact: targetDeviceId } }
      }

      // Fallback facingMode (mobile iOS/Android)
      return { facingMode: this.currentFacingMode }
    },
    
    async switchCamera() {
      // Basculer entre caméra avant et arrière
      this.currentFacingMode = this.currentFacingMode === 'environment' ? 'user' : 'environment'
      
      // Libérer uniquement les tracks sans passer par stopCamera()
      // (stopCamera() met cameraActive=false ce qui retire <video> du DOM)
      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop())
        this.stream = null
      }
      this.videoResolution = null
      
      // Redémarrer la caméra avec le nouveau mode (utilisera deviceId si disponible)
      await this.requestCameraAccess(this.currentFacingMode)
    },
    
    async requestCameraAccess(facingMode = null) {
      this.isRequestingCamera = true
      this.error = null
      
      // Libérer le stream précédent
      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop())
        this.stream = null
      }
      
      // Si un mode spécifique est fourni, l'utiliser
      if (facingMode) {
        this.currentFacingMode = facingMode
      }
      
      try {
        // Vérifier le contexte sécurisé
        const isSecureContext = window.isSecureContext || 
                               window.location.protocol === 'https:' || 
                               window.location.hostname === 'localhost' ||
                               window.location.hostname === '127.0.0.1'
        if (!isSecureContext) throw new Error('HTTPS_REQUIRED')
        
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          throw new Error('API getUserMedia non disponible')
        }

        // === PHASE 1: Obtenir un premier stream pour débloquer les permissions ===
        // Sur Windows, les labels des caméras ne sont visibles QU'APRÈS la première permission.
        // On obtient n'importe quel stream d'abord pour débloquer les permissions,
        // puis on identifie la bonne caméra par label, et on re-demande si nécessaire.

        // Si on a déjà les deviceIds (2ème appel ou après switch), aller directement au bon deviceId
        const targetDeviceId = this.currentFacingMode === 'environment'
          ? this.rearCameraDeviceId
          : this.frontCameraDeviceId

        if (targetDeviceId) {
          // Chemin rapide: on connaît déjà le deviceId de la caméra voulue
          try {
            this.stream = await navigator.mediaDevices.getUserMedia({
              video: { deviceId: { exact: targetDeviceId } },
              audio: false
            })
          } catch (err) {
            console.warn('deviceId exact échoué, tentative sans exact:', err.message)
            this.stream = await navigator.mediaDevices.getUserMedia({
              video: { deviceId: targetDeviceId },
              audio: false
            })
          }
        } else {
          // Premier appel: on ne connaît pas encore les deviceIds
          // Obtenir un stream quelconque pour débloquer les permissions
          let lastError = null
          const initialAttempts = [
            // Essayer d'abord avec facingMode (marche sur mobile)
            { video: { facingMode: { exact: this.currentFacingMode } }, audio: false },
            { video: { facingMode: this.currentFacingMode }, audio: false },
            // Sur Windows: n'importe quelle caméra pour débloquer la permission
            { video: true, audio: false }
          ]
          for (const attempt of initialAttempts) {
            try {
              this.stream = await navigator.mediaDevices.getUserMedia(attempt)
              if (this.stream) { break }
            } catch (err) {
              lastError = err
              if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') throw err
            }
          }
          if (!this.stream) throw (lastError || new Error('Impossible d\'obtenir un stream'))

          // === PHASE 2: Identifier les caméras par label (maintenant disponibles) ===
          const cameras = await this.enumerateCameras()
          
          if (cameras.length > 1 && !this.rearCameraDeviceId) {
            this.identifyCamerasByLabel(cameras)
          }

          // === PHASE 3: Si on a identifié la caméra arrière et qu'on ne l'a pas encore ===
          // Vérifier si le stream actuel est bien la caméra voulue
          const currentDeviceId = this.stream.getVideoTracks()[0]?.getSettings()?.deviceId
          const wantedDeviceId = this.currentFacingMode === 'environment'
            ? this.rearCameraDeviceId
            : this.frontCameraDeviceId

          if (wantedDeviceId && currentDeviceId && currentDeviceId !== wantedDeviceId) {
            // Fermer le stream actuel et ouvrir la bonne caméra
            this.stream.getTracks().forEach(t => t.stop())
            this.stream = null
            try {
              this.stream = await navigator.mediaDevices.getUserMedia({
                video: { deviceId: { exact: wantedDeviceId } },
                audio: false
              })
            } catch (err) {
              console.warn('Impossible de corriger la caméra, fallback:', err.message)
              // Réouvrir n'importe quelle caméra comme fallback
              this.stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false })
            }
          }
        }
        
        // === PHASE 4: Récupérer les infos du stream final ===
        const videoTrack = this.stream.getVideoTracks()[0]
        if (videoTrack) {
          const settings = videoTrack.getSettings()
          if (settings.width && settings.height) {
            this.videoResolution = `${settings.width}x${settings.height}`
          }
          // Sur mobile: mettre à jour le facingMode réel si disponible
          if (settings.facingMode) {
            this.currentFacingMode = settings.facingMode
          }
        }

        // Re-énumérer pour avoir le count à jour (pour canSwitchCamera)
        await this.enumerateCameras()
        
        // Activer la caméra (affiche l'élément <video> dans le DOM)
        this.cameraActive = true
        await this.$nextTick()
        
        // Connecter le flux à l'élément vidéo
        if (this.$refs.videoElement && this.stream) {
          this.$refs.videoElement.srcObject = this.stream
          await new Promise((resolve, reject) => {
            const timeoutId = setTimeout(() => {
              console.warn('Timeout onloadedmetadata — on continue quand même')
              resolve()
            }, 5000)
            this.$refs.videoElement.onloadedmetadata = () => {
              clearTimeout(timeoutId)
              resolve()
            }
            this.$refs.videoElement.onerror = (err) => {
              clearTimeout(timeoutId)
              console.error('Erreur élément vidéo:', err)
              reject(new Error('Erreur lors du chargement du flux vidéo'))
            }
          })
        } else {
          throw new Error('Impossible de connecter le flux vidéo')
        }
        
      } catch (err) {
        console.error('Erreur d\'accès à la caméra:', err)
        
        const fileSelectionSuffix = this.canSelectFile 
          ? ' Vous pouvez sélectionner une photo depuis vos fichiers.' 
          : ' Veuillez réessayer.'
        
        if (err.message === 'HTTPS_REQUIRED') {
          this.error = this.canSelectFile
            ? 'L\'accès à la caméra nécessite une connexion HTTPS sécurisée. Vous pouvez sélectionner une photo depuis vos fichiers.'
            : 'L\'accès à la caméra nécessite une connexion HTTPS sécurisée.'
        } else if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          this.error = this.canSelectFile
            ? 'Accès à la caméra refusé. Veuillez autoriser l\'accès dans les paramètres de votre navigateur ou sélectionner une photo.'
            : 'Accès à la caméra refusé. Veuillez autoriser l\'accès dans les paramètres de votre navigateur.'
        } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
          this.error = this.canSelectFile
            ? 'Aucune caméra détectée sur cet appareil. Vous pouvez sélectionner une photo depuis vos fichiers.'
            : 'Aucune caméra détectée sur cet appareil.'
        } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
          this.error = this.canSelectFile
            ? 'La caméra est déjà utilisée par une autre application. Fermez les autres applications utilisant la caméra ou sélectionnez une photo.'
            : 'La caméra est déjà utilisée par une autre application. Fermez les autres applications utilisant la caméra.'
        } else if (err.message === 'API getUserMedia non disponible') {
          this.error = this.canSelectFile
            ? 'Votre navigateur ne supporte pas l\'accès à la caméra. Veuillez sélectionner une photo depuis vos fichiers.'
            : 'Votre navigateur ne supporte pas l\'accès à la caméra.'
        } else if (err.message === 'Impossible de connecter le flux vidéo') {
          this.error = this.canSelectFile
            ? 'Erreur : impossible de connecter le flux vidéo. Veuillez réessayer ou sélectionner une photo.'
            : 'Erreur : impossible de connecter le flux vidéo. Veuillez réessayer.'
        } else {
          this.error = `Erreur d'accès à la caméra : ${err.message || err.name || 'erreur inconnue'}.${fileSelectionSuffix}`
          console.error('Détails de l\'erreur:', err)
        }
        
        this.cameraActive = false
        this.videoResolution = null
      } finally {
        this.isRequestingCamera = false
      }
    },
    
    switchToFileSelection() {
      this.stopCamera()
      this.cameraActive = false
      this.error = null
      this.videoResolution = null
    },
    
    triggerFileInput() {
      if (this.$refs.fileInput) {
        this.$refs.fileInput.click()
      }
    },
    
    handleFileSelect(event) {
      const file = event.target.files[0]
      if (!file) return
      
      // Vérifier que c'est une image
      if (!file.type.startsWith('image/')) {
        this.error = 'Veuillez sélectionner un fichier image valide.'
        return
      }
      
      // Lire le fichier
      const reader = new FileReader()
      reader.onload = (e) => {
        this.capturedImage = e.target.result
        this.error = null
      }
      reader.onerror = () => {
        this.error = 'Erreur lors de la lecture du fichier.'
      }
      reader.readAsDataURL(file)
    },
    
    /**
     * Ajoute un watermark avec la date sur l'image
     */
    addWatermark(canvas, context) {
      const now = new Date()
      const dateStr = now.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
      const timeStr = now.toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit'
      })
      const watermarkText = `${dateStr} ${timeStr}`
      
      // Configuration du texte
      const fontSize = Math.max(20, Math.floor(canvas.width / 50))
      context.font = `bold ${fontSize}px Arial`
      context.textAlign = 'right'
      context.textBaseline = 'bottom'
      
      // Mesurer le texte
      const metrics = context.measureText(watermarkText)
      const textWidth = metrics.width
      const textHeight = fontSize
      
      // Position en bas à droite avec marges
      const padding = 15
      const x = canvas.width - padding
      const y = canvas.height - padding
      
      // Dessiner un fond semi-transparent pour le watermark
      context.fillStyle = 'rgba(0, 0, 0, 0.6)'
      context.fillRect(
        x - textWidth - padding,
        y - textHeight - padding / 2,
        textWidth + padding * 2,
        textHeight + padding
      )
      
      // Dessiner le texte en blanc
      context.fillStyle = 'rgba(255, 255, 255, 0.95)'
      context.fillText(watermarkText, x, y)
      
      // Ajouter une petite icône horloge
      context.font = `${fontSize}px "Font Awesome 5 Free"`
      context.fillText('🕐', x - textWidth - padding - 5, y)
      
    },
    
    capturePhoto() {
      if (!this.$refs.videoElement || !this.$refs.canvasElement) {
        this.error = 'Erreur lors de la capture de la photo.'
        return
      }
      
      const video = this.$refs.videoElement
      const canvas = this.$refs.canvasElement
      
      // Définir les dimensions du canvas selon la vidéo
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      
      
      // Dessiner l'image de la vidéo sur le canvas
      const context = canvas.getContext('2d')
      context.drawImage(video, 0, 0, canvas.width, canvas.height)
      
      // Ajouter le watermark avec la date
      this.addWatermark(canvas, context)
      
      // Convertir en data URL avec haute qualité
      this.capturedImage = canvas.toDataURL('image/jpeg', 0.95)
      
      
      // Arrêter la caméra
      this.stopCamera()
    },
    
    retakePhoto() {
      this.capturedImage = null
      this.requestCameraAccess()
    },
    
    async confirmPhoto() {
      if (!this.capturedImage || !this.currentContext) {
        return
      }
      
      // Créer un objet picture
      const picture = {
        id: `pic_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
        file: {
          src: this.capturedImage,
          name: `photo_${Date.now()}.jpg`,
          alt: `photo_${Date.now()}.jpg`
        },
        src: this.capturedImage,
        name: `photo_${Date.now()}.jpg`
      }
      
      // Ajouter l'image au store approprié selon le contexte
      const { id, isRestoration, isAction } = this.currentContext
      
      if (isRestoration) {
        // Ajouter à la restauration du résumé (NewVisit)
        await this.$store.dispatch('newVisit/addRestorationPicture', picture)
      } else if (isAction) {
        // Ajouter à un commentaire d'action (ActionsAndWorks)
        // Émettre un événement que ActionsAndWorks écoutera
        window.dispatchEvent(new CustomEvent('camera-photo-captured', { 
          detail: { controlId: id, picture } 
        }))
      } else {
        // Ajouter à un point de contrôle (NewVisit)
        await this.$store.dispatch('newVisit/addControlPointPicture', { id, picture })
      }
      
      
      // Fermer le viewer
      this.handleClose()
    },
    
    stopCamera() {
      if (this.stream) {
        this.stream.getTracks().forEach(track => {
          track.stop()
        })
        this.stream = null
      }
      this.cameraActive = false
      this.videoResolution = null
    },
    
    handleClose() {
      this.stopCamera()
      this.capturedImage = null
      this.error = null
      this.currentContext = null
      
      // Réinitialiser le store (cela mettra isOpen à false via le computed)
      this.$store.dispatch('cameraViewer/setData', null)
    }
  },
  
  beforeUnmount() {
    this.stopCamera()
  }
}
</script>

<style lang="scss">
// Styles pour l'indicateur de résolution
.camera-info {
  position: absolute;
  top: 10px;
  left: 10px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 6px;
  
  i {
    color: #4CAF50;
  }
}

// Les autres styles sont dans src/assets/styles/index.scss
</style>
